import * as React from "react"
import Subtitle from "../../../atoms/typography/Subtitle"
import { HorizontalSpacer, Spacer } from "../../../atoms/spacers"
import { Col, Row, Visible } from "react-grid-system"
import TextInput from "../../TextInput"
import AddressValidationRules from "../../../atoms/inputs/AddressValidationRules"
import Select from "../../Select"
import { useAcceptedCountries } from "../../../../hooks/useAcceptedCountries"
import { CheckCircleIcon } from "../../../atoms/icons/CheckCircleIcon"
import BodyLarge from "../../../atoms/typography/BodyLarge"
import { theme } from "../../../theme"
import SmallColorfulButton from "../../../atoms/buttons/SmallColorfulButton"
import { checkEmailValid } from "../../../../utils/helpers"
import { useRegisterAccount } from "../../../../hooks/useRegisterAccount"
import { BillingInfo } from "../../../pages/products/BookRental"
import Body from "../../../atoms/typography/Body"
import { captureExceptionWithContext } from "../../../../utils/sentry"
import { ApolloError } from "@apollo/client"
import { executeGoogleRecaptcha, useRecaptcha } from "../../../../utils/recaptcha"

interface Props {
  onUserRegistered: (data: BillingInfo) => void
}

const RegisterAccountFormRental: React.FC<Props> = ({ onUserRegistered }) => {
  const { countries, error, loading } = useAcceptedCountries()
  const [registeredUser, setRegisteredUser] = React.useState(false)

  const [formData, setFormData] = React.useState({
    bFirstName: "",
    bLastName: "",
    bStreetAdress1: "",
    bStreetAdress2: "",
    bPostalCode: "",
    bCity: "",
    bCountry: "",
    bCountryArea: undefined,
    bPhone: "",
    bCompany: "",
    bEmail: "",
    password: undefined,
    passwordRep: undefined,
  })

  const [isButtonDisabled, setButtonDisabled] = React.useState(true)
  const areAllFieldsEmpty = () => {
    const values = Object.values(formData)
    return values.every((value) => !value)
  }
  const handleChangeBillingAddressForUnregisteredUser = (event: any) => {
    const name = event.target.name
    const value = event.target.value
    setFormData((values: any) => ({ ...values, [name]: value }))
  }

  // Update the button state whenever form values change
  const handleFormChange = (event: any) => {
    handleChangeBillingAddressForUnregisteredUser(event)
    setButtonDisabled(areAllFieldsEmpty())
  }

  useRecaptcha()

  const onCompletedRegisterAccount = (data: any) => {
    if (data?.registerAccount?.success) {
      console.log("Successfully registered account")

      const billingInfo: BillingInfo = {
        bFirstName: formData.bFirstName,
        bLastName: formData.bLastName,
        bStreetAdress1: formData.bStreetAdress1,
        bStreetAdress2: formData.bStreetAdress2,
        bPostalCode: formData.bPostalCode,
        bCity: formData.bCity,
        bCountry: formData.bCountry,
        bCountryArea: formData.bCountryArea,
        bPhone: formData.bPhone,
        bCompany: formData.bCompany,
        bEmail: formData.bEmail,
      }
      onUserRegistered(billingInfo)
      setRegisteredUser(true)
    } else {
      console.log("could not register account")
      console.log(data)
    }
  }
  const onErrorRegisterAccount = (error: ApolloError) => {
    captureExceptionWithContext(error, "error registering account")
  }
  const {
    registerAccount,
    data,
    loading: registerLoading,
  } = useRegisterAccount({
    onCompleted: (data: any) => onCompletedRegisterAccount(data),
    onError: onErrorRegisterAccount,
  })

  const onRegisterAccount = async (event: any) => {
    event.preventDefault()

    executeGoogleRecaptcha("register")
      .then((token) => {
        try {
          registerAccount({
            variables: {
              company: formData.bCompany,
              email: formData.bEmail,
              firstName: formData.bFirstName,
              lastName: formData.bLastName,
              password1: formData.password,
              password2: formData.passwordRep,
              recaptchaToken: token,
              acceptsTc: true, //user can't get here without accepting terms
            },
          })
        } catch (error) {
          captureExceptionWithContext(error, "error registering account")
        }
      })
      .catch((error) => {
        alert("There was an error with the reCAPTCHA, please try again")
        captureExceptionWithContext(error, "error registering account")
      })
  }

  return (
    <div>
      <Subtitle>Register account</Subtitle>
      <HorizontalSpacer spacing={2} />

      <Row justify="between" align="center">
        <Col md={6} xs={12}>
          <TextInput
            textFieldTitle="First name"
            type="text"
            name="bFirstName"
            id="bFirstName"
            required
            onChange={handleFormChange}
            disabled={registeredUser}
          />
        </Col>
        <Col md={6} xs={12}>
          <Visible xs>
            <HorizontalSpacer spacing={4} />
          </Visible>
          <TextInput
            textFieldTitle="Last name"
            type="text"
            name="bLastName"
            id="bLastName"
            required
            onChange={handleFormChange}
            disabled={registeredUser}
          />
        </Col>
      </Row>
      <HorizontalSpacer spacing={2} />
      <Row justify="between" align="center">
        <Col md={6} xs={12}>
          <TextInput
            textFieldTitle="Email"
            type="text"
            name="bEmail"
            id="bEmail"
            onChange={handleFormChange}
            required
            inputAccepted={checkEmailValid(formData.bEmail)}
            disabled={registeredUser}
          />
        </Col>
        <Col md={6} xs={12}>
          <Visible xs>
            <HorizontalSpacer spacing={4} />
          </Visible>
          <TextInput
            textFieldTitle="Mobile Phone"
            type="tel"
            name="bPhone"
            id="bPhone"
            onChange={handleFormChange}
            required
            disabled={registeredUser}
          />
        </Col>
      </Row>
      <HorizontalSpacer spacing={2} />

      <Row justify="between" align="center">
        <Col md={6} xs={12}>
          <TextInput
            textFieldTitle="Billing address 1"
            type="text"
            name="bStreetAdress1"
            id="bStreetAdress1"
            onChange={handleFormChange}
            required
            disabled={registeredUser}
          />
        </Col>
        <Col md={6} xs={12}>
          <Visible xs>
            <HorizontalSpacer spacing={4} />
          </Visible>
          <TextInput
            textFieldTitle="Billing address 2 (optional)"
            type="text"
            name="bStreetAdress2"
            id="bStreetAdress2"
            onChange={handleFormChange}
            disabled={registeredUser}
          />
        </Col>
      </Row>
      <HorizontalSpacer spacing={2} />

      <Row justify="between" align="center">
        <Col md={6} xs={12}>
          <TextInput
            textFieldTitle="City"
            type="text"
            name="bCity"
            id="bCity"
            onChange={handleFormChange}
            required
            disabled={registeredUser}
          />
        </Col>
        <Col md={6} xs={12}>
          <Visible xs>
            <HorizontalSpacer spacing={4} />
          </Visible>
          <TextInput
            textFieldTitle="Postal code"
            type="text"
            name="bPostalCode"
            id="bPostalCode"
            onChange={handleFormChange}
            required
            disabled={registeredUser}
          />
        </Col>
      </Row>
      <HorizontalSpacer spacing={2} />

      <Row justify="between" align="center">
        <Col md={6} xs={12}>
          <Select
            title="Country"
            options={countries ?? []}
            id="bCountry"
            name="bCountry"
            onChange={handleFormChange}
            required
            disabled={registeredUser}
          />
        </Col>
        <Col md={6} xs={12}>
          <Visible xs>
            <HorizontalSpacer spacing={4} />
          </Visible>
          <TextInput
            textFieldTitle="Company"
            type="text"
            name="bCompany"
            id="bCompany"
            onChange={handleFormChange}
            required
            disabled={registeredUser}
          />
        </Col>
        {formData.bCountry && (
          <Col md={6} xs={12}>
            <AddressValidationRules
              countryCode={formData.bCountry}
              name="bCountryArea"
              id="bCountryArea"
              onChange={handleFormChange}
            />
          </Col>
        )}
      </Row>

      <div>
        <HorizontalSpacer spacing={2} />

        <Row justify="between" align="center">
          <Col md={6} xs={12}>
            <TextInput
              textFieldTitle="Password"
              type="password"
              name="password"
              id="password"
              onChange={handleFormChange}
              required
              disabled={registeredUser}
            />
          </Col>
          <Col md={6} xs={12}>
            <Visible xs>
              <HorizontalSpacer spacing={4} />
            </Visible>
            <TextInput
              textFieldTitle="Repeat password"
              type="password"
              name="passwordRep"
              id="passwordRep"
              onChange={handleFormChange}
              inputAccepted={formData.password && formData.password === formData.passwordRep}
              required
              disabled={registeredUser}
            />
          </Col>
        </Row>

        {data?.registerAccount.errors && (
          <div>
            <HorizontalSpacer spacing={2} />
            {data?.registerAccount.errors.map((error: { field: string; messages: Array<string> }, index: number) => (
              <Body key={index} color={theme.colors.redDark}>
                {error.messages.toString()}
              </Body>
            ))}
          </div>
        )}

        <div>
          <HorizontalSpacer spacing={2} />
          {registeredUser ? (
            <div>
              <HorizontalSpacer spacing={2} />
              <Row justify="center" align="center">
                <CheckCircleIcon />
                <Spacer spacing={1} />
                <BodyLarge color={theme.colors.greenDark}>Account registered</BodyLarge>
              </Row>
              <HorizontalSpacer spacing={2} />
            </div>
          ) : (
            <Row justify="center">
              <SmallColorfulButton
                title="Register and continue to shipping"
                //@ts-ignore
                onClick={(e) => onRegisterAccount(e)}
                variant="yellow"
                disabled={isButtonDisabled || formData.password === undefined}
              />
            </Row>
          )}
        </div>
      </div>
    </div>
  )
}

export default RegisterAccountFormRental
